import React from "react";
import { Check, X, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";

const ClassificationQuestion = ({
  question,
  result,
  currentAnswer,
  submittedFirstTime,
  showExplanation,
  canRetry,
  onAnswerChange,
  onToggleExplanation,
  allQuestions
}) => {

  // Lấy toàn bộ câu classification
  const classificationQuestions =
    allQuestions?.filter((q) => q.question_type === "classification") || [];

  // Chỉ render table một lần (ở câu đầu tiên trong nhóm)
  const isFirst = classificationQuestions[0]?.id === question.id;
  if (!isFirst) return null;

  // ======= NEW: GROUP THEO QUESTION_TEXT =======

  // Tách prefix: phần trước "- Question"
  const getPrefix = (text) => text.split("- Question")[0].trim();

  // Gom nhóm vào object
  const grouped = {};
  classificationQuestions.forEach((q) => {
    const key = getPrefix(q.question_text);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(q);
  });

  // Lấy tên cột
  const columnTitles = Object.keys(grouped);

  // Lấy số dòng lớn nhất
  const maxRows = Math.max(...columnTitles.map((t) => grouped[t].length));

  const handleInputChange = (questionId, value) => {
    if (!submittedFirstTime || canRetry) {
      onAnswerChange(questionId, value);
    }
  };

  const getInputStyle = (questionId) => {
    const questionResult = Array.isArray(result)
      ? result.find((r) => r.id === questionId)
      : result?.id === questionId
        ? result
        : null;

    const disabled = submittedFirstTime && !canRetry;

    let base = "w-full p-2 border-2 rounded text-sm transition-all ";

    base += disabled
      ? "bg-gray-100 border-gray-300 cursor-not-allowed "
      : "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 ";

    if (submittedFirstTime && questionResult) {
      base += questionResult.isCorrect
        ? "border-green-500 bg-green-50 "
        : !canRetry
          ? "border-red-500 bg-red-50 "
          : "";
    }

    return base;
  };

  const getResultIcon = (questionId) => {
    const qResult = Array.isArray(result)
      ? result.find((r) => r.id === questionId)
      : result?.id === questionId
        ? result
        : null;

    if (!submittedFirstTime || !qResult) return null;

    return qResult.isCorrect ? (
      <Check className="w-4 h-4 text-green-600 ml-1" />
    ) : (
      <X className="w-4 h-4 text-red-600 ml-1" />
    );
  };

  const getCurrent = (questionId) => {
    if (typeof currentAnswer === "function") return currentAnswer(questionId);
    return questionId === question.id ? currentAnswer || "" : "";
  };

  return (
    <div className="space-y-6">

      {/* ==== VIDEO SECTION GIỮ NGUYÊN ==== */}
      <div className="w-full bg-gray-100 p-6 rounded-lg">
        {/* Teacher */}
        <div className="flex items-start gap-8 mb-8">
          <div className="flex-shrink-0">
            <div className="w-60 h-[420px] bg-white rounded-lg shadow-sm overflow-hidden">
              <video className="w-full h-full object-cover" controls>
                <source
                  src="/AI_Video/test2_scenario2_reading_task2_teacher.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
          </div>

          <div className="flex-1 space-y-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-start bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
                <p className="text-yellow-700 text-sm leading-relaxed">
                  When you finish reading the findings from the studies, you tell Phuong, a friend in your class, about them and ask her about what the next step for completing the teacher's task should be. Phuong suggests that you should classify the studies according to the trends of their findings. Let's listen to Phuong.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
              <p className="text-blue-800 text-sm leading-relaxed">
                <span className="font-medium">Guidance:</span> Phương, a friend in your class, gives you some suggestions to prepare to write better. Let's listen to Phương.
              </p>
            </div>
          </div>
        </div>

        {/* Student */}
        <div className="flex items-start gap-8 justify-end mb-8">
          <div className="flex-1 max-w-2xl space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
              <p className="text-green-700 text-sm leading-relaxed ">
                Wow, these studies are very good. They are relevant to the topic. You can classify them into some groups to organize your writing clearly. I can see that some studies show good effects, some show bad effects, and some are mixed.
              </p>
            </div>
          </div>

          <div className="flex-shrink-0">
            <div className="w-60 h-[420px] bg-white rounded-lg shadow-sm overflow-hidden">
              <video className="w-full h-full object-cover" controls>
                <source
                  src="/AI_Video/test2_scenario2_reading_task2_student.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
          </div>
        </div>

        <div className="text-sm text-blue-700 bg-blue-50 p-3 rounded-lg">
          💡 <strong>Instructions:</strong> Please classify the studies you have read above into three columns below according to their findings. Please include necessary information properly, following the APA rules of in-text citation for each study.
        </div>
      </div>

      {/* ===== NEW CLASSIFICATION TABLE ===== */}
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>
              {columnTitles.map((title, idx) => (
                <th
                  key={title}
                  className={`p-3 text-center font-semibold border border-gray-300
              ${idx === 0
                      ? "bg-green-100 text-green-800"
                      : idx === 1
                        ? "bg-red-100 text-red-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                >
                  {title}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {Array.from({ length: maxRows }).map((_, rowIdx) => (
              <tr key={rowIdx}>
                {columnTitles.map((title, colIdx) => {
                  const q = grouped[title][rowIdx];
                  return (
                    <td
                      key={title + rowIdx}
                      className={`border p-3
                  ${colIdx === 0
                          ? "bg-green-50"
                          : colIdx === 1
                            ? "bg-red-50"
                            : "bg-yellow-50"
                        }`}
                    >
                      {q && (
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded mr-2">
                              Q{q.question_number}
                            </span>
                            {getResultIcon(q.id)}
                          </div>

                          <input
                            type="text"
                            value={getCurrent(q.id)}
                            onChange={(e) =>
                              handleInputChange(q.id, e.target.value)
                            }
                            disabled={submittedFirstTime && !canRetry}
                            placeholder={
                              rowIdx === 0 ? "e.g., Author (Year)" : ""
                            }
                            className={getInputStyle(q.id)}
                          />
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Example */}
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-sm text-gray-700">
          <strong>💡 Example:</strong> <em>Eidan (2024) - follows APA citation format</em>
        </p>
      </div>

      {/* ==== Kết quả, feedback giữ nguyên ==== */}
      {submittedFirstTime && (
        <div className="space-y-4">

          {/* Overall results */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">
              📊 Classification Results
            </h4>

            <div className="grid grid-cols-3 gap-4 text-sm">
              {columnTitles.map((title) => (
                <div className="text-center" key={title}>
                  <div className="font-medium text-blue-700">
                    {title}
                  </div>
                  <div className="text-blue-600">
                    {grouped[title]
                      .map((q) => {
                        const r = result?.find((x) => x.id === q.id);
                        return r?.isCorrect ? "✓" : "✗";
                      })
                      .join(" ")}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Correct answers */}
          {classificationQuestions.some((q) => {
            const r = result?.find((x) => x.id === q.id);
            return r && !r.isCorrect && !canRetry;
          }) && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-3">
                  ✅ Correct Answers
                </h4>

                <div className="space-y-2 text-sm">
                  {classificationQuestions.map((q) => {
                    const r = result?.find((x) => x.id === q.id);
                    if (!r || r.isCorrect) return null;

                    return (
                      <div key={q.id} className="flex items-start">
                        <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded mr-2 mt-0.5">
                          Q{q.question_number}
                        </span>
                        <div>
                          <div className="font-medium text-green-700">
                            {q.answer}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Retry */}
          {canRetry &&
            classificationQuestions.some((q) => {
              const r = result?.find((x) => x.id === q.id);
              return r && !r.isCorrect;
            }) && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center">
                  <HelpCircle className="w-4 h-4 text-yellow-600 mr-2" />
                  <span className="text-sm text-yellow-800">
                    You can correct incorrect answers for +0.5 points.
                  </span>
                </div>
              </div>
            )}

          {/* Explanation */}
          <button
            onClick={onToggleExplanation}
            className="w-full flex items-center justify-center py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            <span>Show Detailed Explanations</span>
            {showExplanation ? (
              <ChevronUp className="w-4 h-4 ml-2" />
            ) : (
              <ChevronDown className="w-4 h-4 ml-2" />
            )}
          </button>

          {showExplanation && (
            <div className="space-y-3">
              {classificationQuestions.map((q) => {
                if (!q.feedback) return null;

                const r = result?.find((x) => x.id === q.id);

                return (
                  <div
                    key={q.id}
                    className="p-4 bg-blue-50 border border-blue-200 rounded-lg"
                  >
                    <div className="flex items-start">
                      <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded mr-3 mt-0.5">
                        Q{q.question_number}
                      </span>

                      <div className="flex-1 text-sm text-blue-800">
                        {r?.isCorrect ? (
                          <>
                            <p className="font-semibold mb-1">✅ Correct!</p>
                            <p>{q.feedback.correct}</p>
                          </>
                        ) : (
                          <>
                            <p className="font-semibold mb-1">❌ Incorrect</p>
                            <p>{q.feedback.correct}</p>
                          </>
                        )}

                        {q.feedback.note && (
                          <p className="mt-2 text-xs text-blue-600 italic">
                            📝 Note: {q.feedback.note}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClassificationQuestion;
